import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Search } from "lucide-react";
import Seo from "../components/Seo";
import CtaBand from "../components/CtaBand";
import { Reveal, Eyebrow } from "../components/Reveal";
import { SITE, LOCATIONS, REGION_ORDER, locationsByRegion } from "../data/site";

export default function LocationsIndex() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return LOCATIONS.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.county.toLowerCase().includes(q) ||
        l.region.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <>
      <Seo
        title="Security Services Locations | 120+ Towns & Cities | Xtreme Protection England"
        description="Xtreme Protection covers 120+ towns and cities across all nine regions of England — SIA-licensed security guards, patrols, CCTV and response. Find your local security page."
        keywords="security guards England, security company near me, security services London, Manchester, Birmingham, Leeds, Liverpool, Bristol, security guards by city"
        canonical={`${SITE.domain}/locations`}
      />

      <section className="tactical-grid border-b border-white/10" data-testid="locations-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <Reveal>
            <Eyebrow>The Territory // All Nine England Regions</Eyebrow>
            <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl text-white max-w-4xl">
              Security across 120+ England locations
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed">
              Every official city in England plus every major town — each with a
              dedicated local security page covering all 17 services, local
              response and deployment detail.
            </p>
            <div className="mt-8 max-w-xl relative flex border border-white/15 bg-ink focus-within:border-amber transition-colors duration-300">
              <div className="flex items-center pl-4 text-gray-500">
                <Search className="w-4 h-4" />
              </div>
              <input
                data-testid="locations-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by town, city, county or region…"
                autoComplete="off"
                className="flex-1 bg-transparent px-4 py-4 text-sm text-white placeholder-gray-600 outline-none font-body"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-testid="locations-directory">
        {filtered ? (
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-amber mb-6" data-testid="locations-filter-count">
              {filtered.length} matching location{filtered.length === 1 ? "" : "s"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {filtered.map((loc) => (
                <Link
                  key={loc.slug}
                  to={`/locations/${loc.slug}`}
                  data-testid={`location-result-${loc.slug}`}
                  className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-5 py-4 hover:border-amber transition-colors duration-300"
                >
                  <span>
                    <span className="block font-display font-bold uppercase tracking-wide text-lg text-white group-hover:text-amber transition-colors">
                      Security {loc.name}
                    </span>
                    <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                      {loc.county} — {loc.region}
                    </span>
                  </span>
                  <MapPin className="w-4 h-4 text-amber shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        ) : (
          REGION_ORDER.map((region) => (
            <div key={region} className="mb-12" data-testid={`region-${region.toLowerCase().replaceAll(" ", "-")}`}>
              <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white mb-2">
                {region}
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-5">
                {locationsByRegion(region).length} locations covered
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {locationsByRegion(region).map((loc) => (
                  <Link
                    key={loc.slug}
                    to={`/locations/${loc.slug}`}
                    data-testid={`location-card-${loc.slug}`}
                    className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-4 py-3.5 hover:border-amber transition-colors duration-300"
                  >
                    <span>
                      <span className="block font-display font-bold uppercase tracking-wide text-base text-white group-hover:text-amber transition-colors">
                        {loc.name}
                      </span>
                      <span className="block font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-0.5">
                        {loc.county}
                      </span>
                    </span>
                    <MapPin className="w-3.5 h-3.5 text-amber shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <CtaBand
        title="Your town not listed? We still cover it."
        text="Our 120+ location pages are the major hubs — our patrols and officers cover every postcode in England. Call the command centre to confirm coverage for your exact site."
      />
    </>
  );
}
